// Textos de reportsRbac (FE-03a, R06). Ligado em DICT.<lang>.app.reportsRbac; lido por `useApp().t.app.reportsRbac`.
// Relatório "Usuários e permissões (RBAC)": tabelas, matriz permissão × grupo, gavetas, XLSX e impressão.

const pt = {
  locale: 'pt-BR',
  backToHub: '← Central de Relatórios',
  close: 'Fechar',
  refresh: 'Atualizar',
  yes: 'Sim',
  no: 'Não',
  active: 'Ativo',
  inactive: 'Inativo',
  total: 'Total',
  typeSystem: 'Sistema',
  typeCustom: 'Personalizado',
  cat: {
    dashboard: 'Dashboard', olts: 'OLTs', onus: 'ONUs', customers: 'Clientes',
    alerts: 'Alertas', tasks: 'Tarefas', inventory: 'Inventário', users: 'Usuários e grupos',
    audit: 'Auditoria', reports: 'Relatórios', billing: 'Faturamento', settings: 'Configurações',
  },
  role: { owner: 'Proprietário', admin: 'Administrador', operator: 'Operador', viewer: 'Visualizador' },
  origin: {
    role: (r) => `papel · ${r}`,
    direct: 'concessão direta',
    group: (n) => `grupo · ${n}`,
    wildcard: (n) => `curinga · ${n}`,
    full: 'acesso total',
  },
  userDrawer: {
    status: 'Status',
    twoFa: '2FA',
    lastLogin: 'Último login',
    groups: 'Grupos:',
    noGroup: 'sem grupo',
    fullAccess: 'Acesso total — este usuário tem todas as permissões do sistema.',
    matrixTitle: 'Matriz de permissões efetivas',
  },
  groupDrawer: {
    system: 'Grupo do sistema',
    custom: 'Grupo personalizado',
    defaultSuffix: ' · padrão',
    memberCount: (n) => ` · ${n} membro(s)`,
    wildcard: 'Curinga (*) — concede todas as permissões do sistema.',
    granted: 'Permissões concedidas',
    risk: 'risco',
    membersTitle: (n) => `Membros (${n})`,
    noMembers: 'Nenhum usuário neste grupo.',
    inactiveTag: 'inativo',
  },
  matrix: {
    permission: 'Permissão',
    headerTitle: (name, isSystem, n) => `${name} · ${isSystem ? 'sistema' : 'custom'} · ${n} membro(s)`,
    grants: 'concede',
    dangerous: 'permissão perigosa',
    tip: 'Ótimo para auditar “quem pode deletar OLT”.',
    empty: 'Nenhum grupo para comparar.',
  },
  col: {
    user: 'Usuário', role: 'Papel', status: 'Status', groups: 'Grupos', permissions: 'Permissões', twoFa: '2FA',
    lastLogin: 'Último login', mustChange: 'Trocar senha', pending: 'Pendente',
    group: 'Grupo', type: 'Tipo', members: 'Membros', dangerous: 'Perigosas', default: 'Padrão',
    noGroup: 'sem grupo',
  },
  page: {
    title: 'Usuários e permissões (RBAC)',
    subtitle: 'Auditoria de quem pode fazer o quê: usuários e suas permissões efetivas, grupos e a matriz permissão × grupo. As permissões efetivas já vêm resolvidas do backend (proprietário/administrador e curinga contam como acesso total).',
    kpiActive: 'Usuários ativos',
    kpiAdmins: 'Admins / proprietários',
    kpiNoGroup: 'Usuários sem grupo',
    kpiNo2fa: 'Usuários sem 2FA',
    kpiDangerous: 'Perigosas em grupos custom',
    loading: 'Carregando RBAC…',
    tabUsers: (n) => `Usuários (${n})`,
    tabGroups: (n) => `Grupos (${n})`,
    tabMatrix: 'Matriz permissão × grupo',
    emptyUsers: 'Nenhum usuário.',
    usersFooter: (n) => `${n} usuários · clique numa linha para ver a matriz de permissões efetivas e a origem de cada uma.`,
    emptyGroups: 'Nenhum grupo.',
    groupsFooter: (n) => `${n} grupos · clique numa linha para ver as permissões por categoria e os membros.`,
  },
  xlsx: {
    sheetUsers: 'Usuários',
    sheetGroups: 'Grupos',
    sheetMatrix: 'Matriz permissão x grupo',
    sheetEffective: 'Permissões efetivas',
    users: ['Usuário', 'E-mail', 'Papel', 'Status', 'Grupos', 'Nº permissões efetivas', '2FA', 'Último login', 'Deve trocar senha'],
    groups: ['Grupo', 'Tipo', 'Membros', 'Nº permissões', 'Permissões perigosas', 'Padrão'],
    matrix: ['Categoria', 'Permissão', 'Chave', 'Perigosa'],
    effective: ['Usuário', 'E-mail', 'Permissão', 'Chave'],
    noPerms: '— sem permissões —',
  },
  print: {
    users: 'Usuários',
    usersHead: ['Usuário', 'E-mail', 'Papel', 'Status', 'Grupos', 'Permissões', '2FA', 'Último login'],
    groups: 'Grupos',
    groupsHead: ['Grupo', 'Tipo', 'Membros', 'Permissões', 'Padrão'],
    matrix: 'Matriz permissão × grupo',
    matrixHead: 'Permissão',
  },
};

const en = {
  locale: 'en-US',
  backToHub: '← Reports Center',
  close: 'Close',
  refresh: 'Refresh',
  yes: 'Yes',
  no: 'No',
  active: 'Active',
  inactive: 'Inactive',
  total: 'All',
  typeSystem: 'System',
  typeCustom: 'Custom',
  cat: {
    dashboard: 'Dashboard', olts: 'OLTs', onus: 'ONUs', customers: 'Customers',
    alerts: 'Alerts', tasks: 'Tasks', inventory: 'Inventory', users: 'Users and groups',
    audit: 'Audit', reports: 'Reports', billing: 'Billing', settings: 'Settings',
  },
  role: { owner: 'Owner', admin: 'Administrator', operator: 'Operator', viewer: 'Viewer' },
  origin: {
    role: (r) => `role · ${r}`,
    direct: 'direct grant',
    group: (n) => `group · ${n}`,
    wildcard: (n) => `wildcard · ${n}`,
    full: 'full access',
  },
  userDrawer: {
    status: 'Status',
    twoFa: '2FA',
    lastLogin: 'Last login',
    groups: 'Groups:',
    noGroup: 'no group',
    fullAccess: 'Full access — this user has every permission in the system.',
    matrixTitle: 'Effective permissions matrix',
  },
  groupDrawer: {
    system: 'System group',
    custom: 'Custom group',
    defaultSuffix: ' · default',
    memberCount: (n) => ` · ${n} member(s)`,
    wildcard: 'Wildcard (*) — grants every permission in the system.',
    granted: 'Granted permissions',
    risk: 'risk',
    membersTitle: (n) => `Members (${n})`,
    noMembers: 'No user in this group.',
    inactiveTag: 'inactive',
  },
  matrix: {
    permission: 'Permission',
    headerTitle: (name, isSystem, n) => `${name} · ${isSystem ? 'system' : 'custom'} · ${n} member(s)`,
    grants: 'grants',
    dangerous: 'dangerous permission',
    tip: 'Great for auditing “who can delete an OLT”.',
    empty: 'No group to compare.',
  },
  col: {
    user: 'User', role: 'Role', status: 'Status', groups: 'Groups', permissions: 'Permissions', twoFa: '2FA',
    lastLogin: 'Last login', mustChange: 'Change password', pending: 'Pending',
    group: 'Group', type: 'Type', members: 'Members', dangerous: 'Dangerous', default: 'Default',
    noGroup: 'no group',
  },
  page: {
    title: 'Users and permissions (RBAC)',
    subtitle: 'Audit of who can do what: users and their effective permissions, groups and the permission × group matrix. Effective permissions are already resolved by the backend (owner/administrator and wildcard count as full access).',
    kpiActive: 'Active users',
    kpiAdmins: 'Admins / owners',
    kpiNoGroup: 'Users without a group',
    kpiNo2fa: 'Users without 2FA',
    kpiDangerous: 'Dangerous in custom groups',
    loading: 'Loading RBAC…',
    tabUsers: (n) => `Users (${n})`,
    tabGroups: (n) => `Groups (${n})`,
    tabMatrix: 'Permission × group matrix',
    emptyUsers: 'No users.',
    usersFooter: (n) => `${n} users · click a row to see the effective permissions matrix and the origin of each one.`,
    emptyGroups: 'No groups.',
    groupsFooter: (n) => `${n} groups · click a row to see the permissions by category and the members.`,
  },
  xlsx: {
    sheetUsers: 'Users',
    sheetGroups: 'Groups',
    sheetMatrix: 'Permission x group matrix',
    sheetEffective: 'Effective permissions',
    users: ['User', 'E-mail', 'Role', 'Status', 'Groups', 'No. of effective permissions', '2FA', 'Last login', 'Must change password'],
    groups: ['Group', 'Type', 'Members', 'No. of permissions', 'Dangerous permissions', 'Default'],
    matrix: ['Category', 'Permission', 'Key', 'Dangerous'],
    effective: ['User', 'E-mail', 'Permission', 'Key'],
    noPerms: '— no permissions —',
  },
  print: {
    users: 'Users',
    usersHead: ['User', 'E-mail', 'Role', 'Status', 'Groups', 'Permissions', '2FA', 'Last login'],
    groups: 'Groups',
    groupsHead: ['Group', 'Type', 'Members', 'Permissions', 'Default'],
    matrix: 'Permission × group matrix',
    matrixHead: 'Permission',
  },
};

const es = {
  locale: 'es-ES',
  backToHub: '← Centro de Reportes',
  close: 'Cerrar',
  refresh: 'Actualizar',
  yes: 'Sí',
  no: 'No',
  active: 'Activo',
  inactive: 'Inactivo',
  total: 'Total',
  typeSystem: 'Sistema',
  typeCustom: 'Personalizado',
  cat: {
    dashboard: 'Dashboard', olts: 'OLTs', onus: 'ONUs', customers: 'Clientes',
    alerts: 'Alertas', tasks: 'Tareas', inventory: 'Inventario', users: 'Usuarios y grupos',
    audit: 'Auditoría', reports: 'Reportes', billing: 'Facturación', settings: 'Configuración',
  },
  role: { owner: 'Propietario', admin: 'Administrador', operator: 'Operador', viewer: 'Visualizador' },
  origin: {
    role: (r) => `rol · ${r}`,
    direct: 'concesión directa',
    group: (n) => `grupo · ${n}`,
    wildcard: (n) => `comodín · ${n}`,
    full: 'acceso total',
  },
  userDrawer: {
    status: 'Estado',
    twoFa: '2FA',
    lastLogin: 'Último inicio de sesión',
    groups: 'Grupos:',
    noGroup: 'sin grupo',
    fullAccess: 'Acceso total — este usuario tiene todos los permisos del sistema.',
    matrixTitle: 'Matriz de permisos efectivos',
  },
  groupDrawer: {
    system: 'Grupo del sistema',
    custom: 'Grupo personalizado',
    defaultSuffix: ' · predeterminado',
    memberCount: (n) => ` · ${n} miembro(s)`,
    wildcard: 'Comodín (*) — concede todos los permisos del sistema.',
    granted: 'Permisos concedidos',
    risk: 'riesgo',
    membersTitle: (n) => `Miembros (${n})`,
    noMembers: 'Ningún usuario en este grupo.',
    inactiveTag: 'inactivo',
  },
  matrix: {
    permission: 'Permiso',
    headerTitle: (name, isSystem, n) => `${name} · ${isSystem ? 'sistema' : 'personalizado'} · ${n} miembro(s)`,
    grants: 'concede',
    dangerous: 'permiso peligroso',
    tip: 'Ideal para auditar “quién puede eliminar una OLT”.',
    empty: 'Ningún grupo para comparar.',
  },
  col: {
    user: 'Usuario', role: 'Rol', status: 'Estado', groups: 'Grupos', permissions: 'Permisos', twoFa: '2FA',
    lastLogin: 'Último inicio de sesión', mustChange: 'Cambiar contraseña', pending: 'Pendiente',
    group: 'Grupo', type: 'Tipo', members: 'Miembros', dangerous: 'Peligrosos', default: 'Predeterminado',
    noGroup: 'sin grupo',
  },
  page: {
    title: 'Usuarios y permisos (RBAC)',
    subtitle: 'Auditoría de quién puede hacer qué: usuarios y sus permisos efectivos, grupos y la matriz permiso × grupo. Los permisos efectivos ya vienen resueltos desde el backend (propietario/administrador y comodín cuentan como acceso total).',
    kpiActive: 'Usuarios activos',
    kpiAdmins: 'Admins / propietarios',
    kpiNoGroup: 'Usuarios sin grupo',
    kpiNo2fa: 'Usuarios sin 2FA',
    kpiDangerous: 'Peligrosos en grupos personalizados',
    loading: 'Cargando RBAC…',
    tabUsers: (n) => `Usuarios (${n})`,
    tabGroups: (n) => `Grupos (${n})`,
    tabMatrix: 'Matriz permiso × grupo',
    emptyUsers: 'Ningún usuario.',
    usersFooter: (n) => `${n} usuarios · haga clic en una fila para ver la matriz de permisos efectivos y el origen de cada uno.`,
    emptyGroups: 'Ningún grupo.',
    groupsFooter: (n) => `${n} grupos · haga clic en una fila para ver los permisos por categoría y los miembros.`,
  },
  xlsx: {
    sheetUsers: 'Usuarios',
    sheetGroups: 'Grupos',
    sheetMatrix: 'Matriz permiso x grupo',
    sheetEffective: 'Permisos efectivos',
    users: ['Usuario', 'E-mail', 'Rol', 'Estado', 'Grupos', 'N.º de permisos efectivos', '2FA', 'Último inicio de sesión', 'Debe cambiar contraseña'],
    groups: ['Grupo', 'Tipo', 'Miembros', 'N.º de permisos', 'Permisos peligrosos', 'Predeterminado'],
    matrix: ['Categoría', 'Permiso', 'Clave', 'Peligroso'],
    effective: ['Usuario', 'E-mail', 'Permiso', 'Clave'],
    noPerms: '— sin permisos —',
  },
  print: {
    users: 'Usuarios',
    usersHead: ['Usuario', 'E-mail', 'Rol', 'Estado', 'Grupos', 'Permisos', '2FA', 'Último inicio de sesión'],
    groups: 'Grupos',
    groupsHead: ['Grupo', 'Tipo', 'Miembros', 'Permisos', 'Predeterminado'],
    matrix: 'Matriz permiso × grupo',
    matrixHead: 'Permiso',
  },
};

export const REPORTS_RBAC_I18N = { pt, en, es };
